import {Component, HostListener, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {User, UserGroup} from '../../../model/user';
import {UsersService} from '../../../services/users.service';
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {noWhitespaces} from "../../../shared/custom-validators";
import {validateOptionalEmail} from "../../../common/validations";
import {MachineGroup} from "../../../model/machine-group";
import {MachineGroupsService} from "../../../services/machine-groups.service";
import {ComponentCanDeactivate} from "../../../common/can-deactivate/component-can-deactivate";
import {CommonMessagesService} from "../../../services/common-messages.service";
import {UserGroupsPersistRequest} from "../../../model/requests/user-groups-persist-request";

@Component({
  selector: 'app-users-groups',
  templateUrl: './user-groups.component.html',
  styleUrls: ['./user-groups.component.scss']
})
export class UserGroupsComponent implements OnInit, ComponentCanDeactivate {

  public isUserGroupLoading: boolean = true;

  public userForm: FormGroup;
  public persistingChanges: boolean;
  public loadedData: boolean;

  public availableGroups: MachineGroup[] = [];
  public targetGroups: MachineGroup[] = [];

  private user: User;
  private allGroups: MachineGroup[] = [];
  private formSuccess: boolean;


  constructor(private usersService: UsersService,
              private route: ActivatedRoute,
              private router: Router,
              private formBuilder: FormBuilder,
              private machineGroupsService: MachineGroupsService,
              private commonMessagesService: CommonMessagesService) {
  }

  ngOnInit(): void {
    this.loadData();
  }

  private loadData(): void {
    this.isUserGroupLoading = true;
    this.route.url.subscribe(urlSegment => {
      const userUuid: string = urlSegment[0].path;
      const promises: Promise<void>[] = [
        this.usersService.getUserSummary(userUuid).toPromise().then(response => {
          this.user = response.user;
          this.createForm(this.user);
        }, error => {
          if (error.status === 404) {
            this.router.navigate(['/not-found']);
          }
        }),
        this.machineGroupsService.getGroupsSummary().toPromise().then(response => {
          this.allGroups = response.machineGroups;
        })
      ];

      Promise.all(promises).then((): void => {
        if (this.user.groups != null && this.user.groups.length > 0) {
          let targetGroupsUuids: string[] = this.user.groups.map((group: UserGroup) => group.machineGroupUuid);
          this.availableGroups = this.allGroups.filter(group => !targetGroupsUuids.includes(group.machineGroupUuid));
          this.targetGroups = this.allGroups.filter(group => targetGroupsUuids.includes(group.machineGroupUuid));
        } else {
          this.availableGroups = this.allGroups;
        }
        this.loadedData = true;
      }).finally(() => this.isUserGroupLoading = false);
    });
  }

  private createForm(user: User): void {
    this.userForm = this.formBuilder.group({
      username: [{
        value: user.username,
        disabled: true
      }, [Validators.required, Validators.maxLength(255), noWhitespaces]],
      email: [{
        value: user.email,
        disabled: true
      }, [Validators.maxLength(255), validateOptionalEmail]],
      organization: [{value: user.organization.name, disabled: true}, [Validators.required, Validators.maxLength(255)]],
    });
    this.userForm.markAsPending();
  }

  persistUserGroupsData(): void {
    this.persistingChanges = true;
    const persistData: UserGroupsPersistRequest = this.buildPersistData();

    this.usersService.assignUserToGroups(persistData).toPromise().then(() => {
      this.commonMessagesService.showSuccessfullyUpdatedMessage();
      this.formSuccess = true;
      this.router.navigate(['/users']);
    }).catch(error => {
      this.commonMessagesService.showPersistingError('edit', error, this.user.username);
      this.persistingChanges = false;
    });
  }

  private buildPersistData(): UserGroupsPersistRequest {
    return {
      uuid: this.user.userUuid,
      groupUuids: this.targetGroups?.length > 0 ? UserGroupsComponent.getGroupUuids(this.targetGroups) : null,
    };
  }

  private static getGroupUuids(groups: MachineGroup[]): string[] {
    return groups.map((group: MachineGroup) => group.machineGroupUuid);
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.formSuccess;
  }

}
